import { createFeatureSelector, createSelector } from '@ngrx/store';
import { isEqual } from 'lodash';
import * as moment from 'moment';

import { SortOrder } from '@app/services/summary.service';
import {
  PeriodType,
  SummaryReportSettingsModel,
  SummaryType,
} from '@main-application/shared/services/summary-report-settings.service';

import { SummaryReportSettingsState } from '../reducers/summary-report-settings.reducers';

export const selectSummaryReportSettingsState =
  createFeatureSelector<SummaryReportSettingsState>('summaryReportSettings');

export const selectSummarySettingsMap = createSelector(
  selectSummaryReportSettingsState,
  state => state.summarySettings
);

export const selectCombinedSummarySetting = (type: SummaryType) =>
  createSelector(selectSummaryReportSettingsState, state => state.summarySettings?.[type] ?? null);

export const selectInitialSummarySetting = (type: SummaryType) =>
  createSelector(selectSummarySettingsMap, settings => settings[type]?.initial);

export const selectModifiedSummarySetting = (type: SummaryType) =>
  createSelector(selectSummarySettingsMap, settings => settings[type]?.modified);

export const selectSummarySettingDirty = (type: SummaryType) =>
  createSelector(selectSummarySettingsMap, settings => {
    const s: { initial: SummaryReportSettingsModel; modified: SummaryReportSettingsModel } = settings[type];
    if (!s?.initial || !s?.modified) return false;

    const { initial, modified } = s;

    const shouldCompareDates = initial.periodType === PeriodType.Custom || modified.periodType === PeriodType.Custom;
    const startDate =
      (initial.startDate as unknown) instanceof Date
        ? moment(initial.startDate).format('YYYY-MM-DD')
        : initial.startDate;
    const endDate =
      (initial.endDate as unknown) instanceof Date ? moment(initial.endDate).format('YYYY-MM-DD') : initial.endDate;
    const modifiedStartDate =
      (modified.startDate as unknown) instanceof Date
        ? moment(modified.startDate).format('YYYY-MM-DD')
        : modified.startDate;
    const modifiedEndDate =
      (modified.endDate as unknown) instanceof Date ? moment(modified.endDate).format('YYYY-MM-DD') : modified.endDate;

    const result =
      initial.name !== modified.name ||
      initial.description !== modified.description ||
      initial.summaryType !== modified.summaryType ||
      initial.dataSource !== modified.dataSource ||
      initial.dataSourceModifier !== modified.dataSourceModifier ||
      initial.periodType !== modified.periodType ||
      initial.reportView !== modified.reportView ||
      initial.sortBy !== modified.sortBy ||
      (shouldCompareDates && (startDate !== modifiedStartDate || endDate !== modifiedEndDate)) ||
      !isEqual(
        initial.showBy,
        modified.showBy?.map(s => ({
          fieldType: s.fieldType,
          isDescending: s.isDescending || s.sortOrder === SortOrder.Descending,
        }))
      ) ||
      !isEqual(initial.filterBy, modified.filterBy) ||
      !isEqual(initial.rolesCanView, modified.rolesCanView) ||
      !isEqual(initial.rolesCanEdit, modified.rolesCanEdit);
    return result;
  });

export const selectAllSummaryReportSettings = createSelector(selectSummaryReportSettingsState, state => state.settings);

export const selectSummaryReportSettingsLoading = createSelector(
  selectSummaryReportSettingsState,
  state => state.loading
);

export const selectSummaryReportSettingsError = createSelector(selectSummaryReportSettingsState, state => state.error);

export const selectIsDownloadingReport = createSelector(selectSummaryReportSettingsState, state => state.downloading);
